import { Button } from "./ui/button";
import { Card } from "./ui/card";
import { Logo } from "./Logo";
import {
  FestiveDecoration,
  FestiveDivider,
} from "./FestiveDecoration";
import {
  Users,
  Heart,
  Sparkles,
  Gift,
  Calendar,
  PartyPopper,
} from "lucide-react";
import { ImageWithFallback } from "./figma/ImageWithFallback";

// TEMPORARY FIX: Hero image file was deleted, using external URL until restored
// TODO: Restore /src/assets/phill-brown-yPyYTG2fYYs-unsplash.jpg from git history
const heroImageUrl = "https://images.unsplash.com/photo-1512909006721-3d6018887383?w=1200";

interface LandingPageProps {
  onNavigate: (screen: string) => void;
  isAuthenticated?: boolean;
}

export function LandingPage({ onNavigate, isAuthenticated }: LandingPageProps) {
  const handleLoginClick = () => {
    // If already authenticated, go to dashboard instead of login
    if (isAuthenticated) {
      onNavigate('dashboard');
    } else {
      onNavigate('login');
    }
  };
  return (
    <div className="min-h-screen bg-gradient-to-b from-white to-secondary/30">
      {/* Hero Section */}
      <section className="relative overflow-hidden">
        <div className="absolute inset-0 bg-gradient-to-br from-primary/5 via-transparent to-accent/20"></div>
        <FestiveDecoration
          variant="snowflakes"
          position="top-left"
          className="opacity-30"
        />
        <FestiveDecoration
          variant="gifts"
          position="bottom-right"
          className="opacity-20"
        />

        <div className="relative max-w-7xl mx-auto px-4 py-4 md:py-24">
          <div className="grid md:grid-cols-2 gap-4 md:gap-12 items-center">
            {/* Text Content */}
            <div className="space-y-2 md:space-y-8">
              <div className="my-0 py-0">
                <Logo size="xl" />
              </div>

              <p className="text-base md:text-xl text-muted-foreground leading-snug md:leading-relaxed text-center pt-0">
                Transform gift-giving into a collaborative
                experience. Pool funds with friends and family
                to give meaningful gifts that truly matter.
              </p>

              <div className="flex flex-col sm:flex-row gap-3 md:gap-4 justify-center">
                <Button
                  size="lg"
                  onClick={() =>
                    onNavigate("create-gift-group")
                  }
                  className="bg-primary hover:bg-primary/90 shadow-lg hover:shadow-xl transition-all"
                >
                  <Gift className="w-5 h-5 mr-2" />
                  Create a Gift Group
                </Button>
                <Button
                  size="lg"
                  variant="outline"
                  onClick={handleLoginClick}
                  className="border-2 border-primary/20 hover:border-primary hover:bg-primary/5"
                >
                  {isAuthenticated ? 'Go to Dashboard' : 'Login'}
                </Button>
              </div>

              <div className="flex items-center justify-center gap-3 md:gap-6 text-xs md:text-sm text-muted-foreground pt-1 md:pt-4">
                <div className="flex items-center gap-1 md:gap-2">
                  <div className="w-1.5 h-1.5 md:w-2 md:h-2 rounded-full bg-primary/60"></div>
                  <span>Simple & fun</span>
                </div>
                <div className="flex items-center gap-1 md:gap-2">
                  <div className="w-1.5 h-1.5 md:w-2 md:h-2 rounded-full bg-primary/60"></div>
                  <span>AI-Enhanced</span>
                </div>
                <div className="flex items-center gap-1 md:gap-2">
                  <div className="w-1.5 h-1.5 md:w-2 md:h-2 rounded-full bg-primary/60"></div>
                  <span>Better Together</span>
                </div>
              </div>
            </div>

            {/* Hero Image */}
            <div className="relative">
              <div className="absolute inset-0 bg-gradient-to-br from-primary/10 to-destructive/10 rounded-3xl blur-3xl"></div>
              <div className="relative rounded-2xl overflow-hidden shadow-2xl w-full aspect-[4/3]">
                <ImageWithFallback
                  src={heroImageUrl}
                  alt="Beautifully wrapped gifts celebrating the joy of giving"
                  className="w-full h-full object-cover"
                />
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Features Section */}
      <section className="py-6 md:py-20 px-4 bg-white relative">
        <FestiveDecoration
          variant="stars"
          position="top-right"
          className="opacity-20"
        />

        <div className="max-w-6xl mx-auto">
          <div className="text-center mb-4 md:mb-16">
            <h2 className="text-3xl md:text-4xl mb-2 md:mb-4">
              How It Works
            </h2>
            <p className="text-base md:text-lg text-muted-foreground max-w-2xl mx-auto">
              Three simple steps to make gift-giving more
              meaningful and collaborative
            </p>
            <FestiveDivider className="mt-3 md:mt-8 max-w-md mx-auto" />
          </div>

          <div className="grid md:grid-cols-3 gap-3 md:gap-8">
            <Card className="p-3 md:p-8 hover:shadow-lg transition-shadow border-2 border-primary/10">
              <div className="flex items-center gap-2 md:gap-3 mb-0 md:mb-0 md:block">
                <div className="bg-primary/10 p-2 md:p-4 rounded-lg md:rounded-xl w-10 h-10 md:w-14 md:h-14 flex items-center justify-center md:mb-6 shrink-0">
                  <Users className="w-5 h-5 md:w-7 md:h-7 text-primary" />
                </div>
                <div className="flex items-center gap-2 md:hidden">
                  <div className="inline-block px-2 py-0.5 bg-primary/10 text-primary rounded-full text-xs">
                    Step 1
                  </div>
                  <h3 className="text-base m-0">
                    Create or Join
                  </h3>
                </div>
              </div>
              <div className="space-y-0.5 md:space-y-3 mt-1 md:mt-0">
                <div className="hidden md:inline-block px-3 py-1 bg-primary/10 text-primary rounded-full text-sm">
                  Step 1
                </div>
                <h3 className="hidden md:block text-xl md:text-2xl">
                  Create or Join
                </h3>
                <p className="text-sm md:text-base text-muted-foreground">
                  Start a gift group for any occasion or join
                  one you've been invited to. Invite friends,
                  family, or colleagues to collaborate.
                </p>
              </div>
            </Card>

            <Card className="p-3 md:p-8 hover:shadow-lg transition-shadow border-2 border-primary/10">
              <div className="flex items-center gap-2 md:gap-3 mb-0 md:mb-0 md:block">
                <div className="bg-ring/20 p-2 md:p-4 rounded-lg md:rounded-xl w-10 h-10 md:w-14 md:h-14 flex items-center justify-center md:mb-6 shrink-0">
                  <Sparkles className="w-5 h-5 md:w-7 md:h-7 text-ring" />
                </div>
                <div className="flex items-center gap-2 md:hidden">
                  <div className="inline-block px-2 py-0.5 bg-ring/20 text-ring rounded-full text-xs">
                    Step 2
                  </div>
                  <h3 className="text-base m-0">
                    Pool & Suggest
                  </h3>
                </div>
              </div>
              <div className="space-y-0.5 md:space-y-3 mt-1 md:mt-0">
                <div className="hidden md:inline-block px-3 py-1 bg-ring/20 text-ring rounded-full text-sm">
                  Step 2
                </div>
                <h3 className="hidden md:block text-xl md:text-2xl">
                  Pool & Suggest
                </h3>
                <p className="text-sm md:text-base text-muted-foreground">
                  We handle collecting the contributions,
                  collecting everyone's suggestions, and adding
                  some great AI matched gifts.
                </p>
              </div>
            </Card>

            <Card className="p-3 md:p-8 hover:shadow-lg transition-shadow border-2 border-primary/10">
              <div className="flex items-center gap-2 md:gap-3 mb-0 md:mb-0 md:block">
                <div className="bg-destructive/10 p-2 md:p-4 rounded-lg md:rounded-xl w-10 h-10 md:w-14 md:h-14 flex items-center justify-center md:mb-6 shrink-0">
                  <Heart className="w-5 h-5 md:w-7 md:h-7 text-destructive" />
                </div>
                <div className="flex items-center gap-2 md:hidden">
                  <div className="inline-block px-2 py-0.5 bg-destructive/10 text-destructive rounded-full text-xs">
                    Step 3
                  </div>
                  <h3 className="text-base m-0">Rank & Gift</h3>
                </div>
              </div>
              <div className="space-y-0.5 md:space-y-3 mt-1 md:mt-0">
                <div className="hidden md:inline-block px-3 py-1 bg-destructive/10 text-destructive rounded-full text-sm">
                  Step 3
                </div>
                <h3 className="hidden md:block text-xl md:text-2xl">
                  Rank & Gift
                </h3>
                <p className="text-sm md:text-base text-muted-foreground">
                  Your group ranks the gift suggestions and then
                  we automatically order your gifts to ensure
                  they arrive on-time and with everyone's
                  personal greeting!
                </p>
              </div>
            </Card>
          </div>
        </div>
      </section>

      {/* Gift Group Types */}
      <section className="bg-gradient-to-b from-secondary/30 to-accent/30 py-6 md:py-20 px-4 relative overflow-hidden">
        <FestiveDecoration
          variant="community"
          position="top-left"
          className="opacity-20"
        />
        <FestiveDecoration
          variant="community"
          position="bottom-right"
          className="opacity-20"
        />

        <div className="max-w-6xl mx-auto relative">
          <div className="text-center mb-4 md:mb-16">
            <h2 className="text-3xl md:text-4xl mb-2 md:mb-4">
              Perfect for Any Occasion
            </h2>
            <p className="text-base md:text-lg text-muted-foreground">
              From birthdays to team celebrations, we've got you
              covered
            </p>
            <FestiveDivider className="mt-3 md:mt-8 max-w-md mx-auto" />
          </div>

          <div className="grid gap-y-4 md:grid-cols-2 lg:grid-cols-3 md:gap-6">
<Card className="p-4 md:p-6 border-primary/20">
  <div className="flex gap-2 md:block">
    <span className="flex-shrink-0">
      <Calendar className="w-6 h-6 md:w-8 md:h-8 text-primary md:mb-3" />
    </span>
    <div className="flex flex-col flex-1">
      <h4 className="mb-0 mt-0 text-base md:text-lg leading-none md:leading-normal md:mb-2">
        Single Giftee
      </h4>
      <p className="text-xs md:text-sm text-muted-foreground mt-2 md:mt-0 !mb-0 leading-tight md:leading-normal">
        One person receives a gift from one or more gifters. Common for birthdays and individual anniversaries.
      </p>
    </div>
  </div>
</Card>
            <Card className="p-4 md:p-6 border-primary/20">
              <div className="flex gap-2 md:block">
                <span className="flex-shrink-0">
                  <Users className="w-6 h-6 md:w-8 md:h-8 text-primary md:mb-3" />
                </span>
                <div className="flex flex-col flex-1">
                  <h4 className="mb-0 mt-0 text-base md:text-lg leading-none md:leading-normal md:mb-2">
                    Multiple Giftees
                  </h4>
                  <p className="text-xs md:text-sm text-muted-foreground mt-2 md:mt-0 !mb-0 leading-tight md:leading-normal">
                    Multiple people receive one shared gift from one or more gifters. Common for wedding anniversaries.
                  </p>
                </div>
              </div>
            </Card>

            <Card className="p-4 md:p-6 border-primary/20">
              <div className="flex gap-2 md:block">
                <span className="flex-shrink-0">
                  <Sparkles className="w-6 h-6 md:w-8 md:h-8 text-ring md:mb-3" />
                </span>
                <div className="flex flex-col flex-1">
                  <h4 className="mb-0 mt-0 text-base md:text-lg leading-none md:leading-normal md:mb-2">
                    Individual Gifts
                  </h4>
                  <p className="text-xs md:text-sm text-muted-foreground mt-2 md:mt-0 !mb-0 leading-tight md:leading-normal">
                    Multiple people receive individual gifts from one or more gifters. Common for team anniversaries and accomplishments.
                  </p>
                </div>
              </div>
            </Card>

            <Card className="p-4 md:p-6 border-primary/20">
              <div className="flex gap-2 md:block">
                <span className="flex-shrink-0">
                  <PartyPopper className="w-6 h-6 md:w-8 md:h-8 text-destructive md:mb-3" />
                </span>
                <div className="flex flex-col flex-1">
                  <h4 className="mb-0 mt-0 text-base md:text-lg leading-none md:leading-normal md:mb-2">
                    Gift Exchange
                  </h4>
                  <p className="text-xs md:text-sm text-muted-foreground mt-2 md:mt-0 !mb-0 leading-tight md:leading-normal">
                    We pair gifters up to purchase gifts for each other (aka Secret Santa).
                  </p>
                </div>
              </div>
            </Card>

            <Card className="p-4 md:p-6 border-primary/20">
              <div className="flex gap-2 md:block">
                <span className="flex-shrink-0">
                  <Gift className="w-6 h-6 md:w-8 md:h-8 text-ring md:mb-3" />
                </span>
                <div className="flex flex-col flex-1">
                  <h4 className="mb-0 mt-0 text-base md:text-lg leading-none md:leading-normal md:mb-2">
                    White Elephant
                  </h4>
                  <p className="text-xs md:text-sm text-muted-foreground mt-2 md:mt-0 !mb-0 leading-tight md:leading-normal">
                    Everyone secretly purchases wacky gifts individually, then at gift time we create a random order to pick an unopened gift or steal an opened gift (3 steal limit).
                  </p>
                </div>
              </div>
            </Card>

            <Card className="p-4 md:p-6 border-primary/20">
              <div className="flex gap-2 md:block">
                <span className="flex-shrink-0">
                  <Heart className="w-6 h-6 md:w-8 md:h-8 text-destructive md:mb-3" />
                </span>
                <div className="flex flex-col flex-1">
                  <h4 className="mb-0 mt-0 text-base md:text-lg leading-none md:leading-normal md:mb-2">
                    Gift Circle
                  </h4>
                  <p className="text-xs md:text-sm text-muted-foreground mt-2 md:mt-0 !mb-0 leading-tight md:leading-normal">
                    Everyone collaborates to pick gifts for every member except themselves!
                  </p>
                </div>
              </div>
            </Card>
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-8 md:py-20 px-4 text-center bg-white">
        <div className="max-w-3xl mx-auto">
          <div className="bg-gradient-to-br from-primary/5 via-accent/10 to-destructive/5 rounded-2xl md:rounded-3xl p-6 md:p-12">
            <h2 className="text-2xl md:text-4xl mb-3 md:mb-4">
              Ready to Give Better Together?
            </h2>
            <p className="text-base md:text-lg text-muted-foreground mb-4 md:mb-8 max-w-xl mx-auto">
              Join thousands making gift-giving more joyful,
              collaborative, and fun
            </p>

            <Button
              size="lg"
              onClick={() => onNavigate("create-gift-group")}
              className="bg-primary hover:bg-primary/90 shadow-lg hover:shadow-xl transition-all px-6 md:px-8"
            >
              <Gift className="w-5 h-5 mr-2" />
              Create Your First Gift Group
            </Button>
          </div>
        </div>
      </section>

      {/* Footer */}
      <footer className="bg-primary/5 border-t border-primary/10 py-6 md:py-12 px-4">
        <div className="max-w-6xl mx-auto">
          <div className="flex flex-col items-center gap-3 md:gap-6">
            <Logo size="md" />
            <p className="text-xs md:text-sm text-muted-foreground text-center max-w-md">
              Inspired by Acts 20:35 - "It is more blessed to
              give than to receive."
            </p>
            {/* Footer Navigation */}
            <div className="flex flex-wrap gap-2 justify-center">
              {/* Empty - admin tools moved to admin dashboard */}
            </div>
            <p className="text-xs text-muted-foreground">
              &copy; 2025 Better to Give than Receive. All
              rights reserved.
            </p>
          </div>
        </div>
      </footer>
    </div>
  );
}